/**
 * KV/IndexedDB Sales Repository (ADR-0009 Phase 2).
 * sale_sync outbox: persistCheckoutAtomically / persistCompletedSale (Dexie TX or after-persist).
 */

import {
  persistCompletedSale,
  persistCheckoutAtomically,
  queueBusinessPayloadWrite
} from '../storage/businessPayloadWrite';
import {
  readSalesFromBestSource,
  refreshSalesReadCache
} from '../../utils/salesReadSource';
import { removeSaleFromAggregateStore } from '../storage/domainAggregateStore';

function saleMutationId(sale) {
  return (
    sale?.clientMutationId ||
    sale?.orderId ||
    sale?.id ||
    `mut_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
  );
}

function scheduleCloudDrain(businessId) {
  void import('../../services/parallelCloudFieldSync')
    .then(({ scheduleAutomaticCloudSync }) => {
      scheduleAutomaticCloudSync(businessId, { reason: 'save' });
    })
    .catch(() => {});
}

export class KvSalesRepository {
  /**
   * Atomarer Checkout: Lots + stockData + Sale in einem Write.
   * @param {string} businessId
   * @param {object} saleData
   * @returns {Promise<import('../../application/ports/salesRepository.port').SaveCompletedSaleResult>}
   */
  async saveCheckoutAtomically(businessId, saleData) {
    const mutationId = String(saleMutationId(saleData));
    const result = await persistCheckoutAtomically(businessId, saleData, {
      saleSyncMutationId: mutationId
    });
    await refreshSalesReadCache(businessId);
    scheduleCloudDrain(businessId);
    return { ...result, outboxId: mutationId };
  }

  async saveCompletedSale(businessId, saleData) {
    const mutationId = String(saleMutationId(saleData));
    const result = await persistCompletedSale(businessId, saleData, {
      saleSyncMutationId: mutationId
    });
    await refreshSalesReadCache(businessId);
    scheduleCloudDrain(businessId);
    return { ...result, outboxId: mutationId };
  }

  /**
   * @param {string} businessId
   * @param {string} saleId
   */
  async removeSale(businessId, saleId) {
    return this.removeSales(businessId, [saleId]);
  }

  /**
   * Full or line void (atomic lots + sale + créance).
   * SSOT: domain/sales/storno → persistSaleVoidAtomically (do not duplicate planning here).
   * @param {string} businessId
   * @param {object} voidRequest
   */
  async voidSale(businessId, voidRequest = {}) {
    const { voidSale: domainVoidSale } = await import('../../domain/sales/storno');
    const result = await domainVoidSale({ ...voidRequest, businessId });
    await refreshSalesReadCache(businessId);
    return result;
  }

  /**
   * @param {string} businessId
   * @param {string[]} saleIds
   */
  async removeSales(businessId, saleIds) {
    const drop = new Set((saleIds || []).map(String).filter(Boolean));
    if (drop.size === 0) {
      return readSalesFromBestSource(businessId);
    }

    let finalSales = [];
    await queueBusinessPayloadWrite(
      businessId,
      (parsed) => {
        const sales = Array.isArray(parsed.sales) ? parsed.sales : [];
        finalSales = sales.filter(
          (s) => !drop.has(String(s.id)) && !drop.has(String(s.orderId))
        );
        parsed.sales = finalSales;
        return parsed;
      },
      { authoritativeFields: ['sales'] }
    );
    for (const id of drop) {
      await removeSaleFromAggregateStore(businessId, id);
    }
    await refreshSalesReadCache(businessId);
    return finalSales;
  }

  /**
   * @param {string} businessId
   */
  async listSales(businessId) {
    return readSalesFromBestSource(businessId);
  }
}
